import axios from 'axios'
import { showNotify } from 'vant'
import { STORAGE_TOKEN_KEY } from '@/stores/mutation-type'
import md5 from 'crypto-js/md5'
import { errorToast } from '@/utils/toast'
import { useUserStore } from '@/stores'
import { locale, serviceLocales, SERVICE_PT } from '@/utils/i18n'

/**
 * ============================================
 * 应用配置（运行时，唯一真相源）
 * ============================================
 * - 刷新页面 = 重新 fetch
 * - 同生命周期内只加载一次
 * - 加载失败 = 应用不可用
 */
let configPromise = null

function loadAppConfig() {
  // console.log('loadAppConfig...')

  if (!configPromise) {
    configPromise = fetch('/config.json')
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`config.json not found (${res.status})`)
        }

        const localConfig = await res.json()

        if (!localConfig.baseApiUrl) {
          throw new Error('config.json missing "baseApiUrl"')
        }
        if (!localConfig.appid) {
          throw new Error('config.json missing "appid"')
        }

        return localConfig
      })
      .catch((err) => {
        console.error('[request] load config.json failed:', err)

        errorToast({
          message: '应用配置加载失败，请刷新页面或联系管理员',
          duration: 0,
        })

        // 重新抛出，阻断后续逻辑
        throw err
      })
  }

  return configPromise
}

// 页面加载即触发（刷新必执行）
loadAppConfig()

// axios 实例（baseURL 初始为空，强制从 config.json 读取）
const request = axios.create({
  baseURL: '',
  timeout: 6000,
})

// 签名函数
function generateSignature(params, secretKey, expireTime) {
  const sortedParams = Object.keys(params || {})
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join(':')

  const baseString = `${sortedParams}bjsignature`
  const signString = `${secretKey}${expireTime}${baseString}`

  return md5(signString).toString()
}

// 请求拦截器
request.interceptors.request.use(async (config) => {
  const localConfig = await loadAppConfig()

  // 只设置一次 baseURL
  if (!config.baseURL) {
    config.baseURL = localConfig.baseApiUrl
  }

  const expireTime = Date.now()
  const secretKey = '78e36ersfdhjhdjf82345@#!23SD14F'

  let params = {}
  if (config.method === 'get' && config.params) {
    params = { ...config.params }
  } else if (['post', 'put', 'patch'].includes(config.method) && config.data) {
    params = { ...config.data }
  }

  const signature = generateSignature(params, secretKey, expireTime)

  config.headers['expire'] = expireTime
  config.headers['sign'] = signature
  config.headers['appid'] = btoa(localConfig.appid)
  config.headers['Accept-Language'] = serviceLocales[locale.value] || SERVICE_PT

  const token = localStorage.getItem(STORAGE_TOKEN_KEY)
  if (token) {
    config.headers['token'] = token
  }

  return config
}, Promise.reject)

// 响应拦截器（业务逻辑基本保持你原有风格）
request.interceptors.response.use(
  (response) => {
    const { status, data } = response

    if (status !== 200) {
      errorToast(data?.msg || 'Request failed')
      return Promise.reject(new Error(data?.msg || 'Request failed'))
    }

    // 特殊业务码
    if ([1011, 1039, 304].includes(data.code)) {
      return data
    }

    // 账号相关退出
    if ([1031, 1038].includes(data.code)) {
      errorToast(data.msg || `${data.code} error`)
      useUserStore().handleLogout()
      return
    }

    if (data.code !== 1) {
      errorToast(data?.msg || 'Request failed')
      return Promise.reject(new Error(data?.msg || 'Request failed'))
    }

    return data
  },
  (error) => {
    const res = error.response || {}

    if (res.status === 403) {
      errorToast(res.data?.message || 'Forbidden')
    } else if (res.status === 401) {
      errorToast('Authorization verification failed')
      useUserStore().handleLogout()
    } else if (res.status === 500) {
      errorToast('Server error')
    } else if (res.status === 404) {
      errorToast('API not found')
    } else if (res.status === 400) {
      errorToast(res.data?.message || 'Bad request')
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.warn('Request timeout')
    }

    return Promise.reject(error)
  }
)

export default request
